import React, { useState, useEffect, useRef, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';

/**
 * PrintlogicCustomerModal component for selecting Printlogic customers
 * Used by managers to assign quotes to external customers
 *
 * Optional email assignment mode:
 * - emailToAssign: Email address to assign to a customer
 * - showEmailAssignment: Enable assignment UI
 * - onAssignEmail: Callback when email is assigned
 */
const PrintlogicCustomerModal = ({
  isOpen,
  onClose,
  onSelectCustomer,
  className,
  emailToAssign,
  showEmailAssignment = false,
  onAssignEmail
}) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Email assignment state
  const [assignmentInProgress, setAssignmentInProgress] = useState(false);
  const [assignmentError, setAssignmentError] = useState('');
  const [assignmentSuccess, setAssignmentSuccess] = useState(false);
  const [showReplaceDialog, setShowReplaceDialog] = useState(false);
  const [selectedCustomerForAssignment, setSelectedCustomerForAssignment] = useState(null);
  const [selectedReplaceSlot, setSelectedReplaceSlot] = useState(null);
  const [timeoutWarning, setTimeoutWarning] = useState(false);
  const [syncInProgress, setSyncInProgress] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  const [syncError, setSyncError] = useState('');

  // Reference for search input autofocus
  const searchInputRef = useRef(null);

  // Autofocus search input when modal opens
  useEffect(() => {
    if (isOpen) {
      // Add small delay to wait for transition animation to complete
      const timeout = setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 350);

      return () => clearTimeout(timeout);
    }
  }, [isOpen]);

  // Debounced search effect
  useEffect(() => {
    if (isOpen) {
      // Clear existing timeout
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
      
      // Set new timeout for debounced search
      const timeout = setTimeout(() => {
        loadCustomers(searchQuery);
      }, searchQuery ? 300 : 0); // 300ms debounce for search, immediate for initial load
      
      setSearchTimeout(timeout);
      
      // Cleanup timeout on unmount or when dependencies change
      return () => {
        if (timeout) {
          clearTimeout(timeout);
        }
      };
    }
  }, [isOpen, searchQuery]);

  // Clear search when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setCustomers([]);
      setError('');
      setSyncMessage('');
      setSyncError('');
    }
  }, [isOpen]);

  const API_URL = process.env.REACT_APP_API_URL || '';

  const loadCustomers = async (search = '') => {
    setLoading(true);
    setError('');

    try {
      const searchParam = search ? `search=${encodeURIComponent(search)}` : '';
      const includeEmailsParam = showEmailAssignment ? 'include_emails=true' : '';
      const params = [searchParam, includeEmailsParam].filter(Boolean).join('&');
      const queryString = params ? `?${params}` : '';

      const response = await fetch(`${API_URL}/api/printlogic-customers${queryString}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Access denied. Manager permissions required.');
        } else {
          throw new Error('Failed to load Printlogic customers');
        }
      }

      const data = await response.json();
      setCustomers(data);
    } catch (err) {
      setError(err.message || 'Failed to load customers');
      console.error('Error loading Printlogic customers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCustomer = (customer) => {
    // If in assignment mode, handle assignment instead of regular selection
    if (showEmailAssignment && emailToAssign) {
      const emailCount = customer.emails ? customer.emails.length : 0;

      if (emailCount >= 10) {
        // Show replacement dialog
        setSelectedCustomerForAssignment(customer);
        setShowReplaceDialog(true);
      } else {
        // Assign directly
        handleAssignEmail(customer);
      }
    } else {
      // Standard selection (no assignment)
      onSelectCustomer(customer);
      onClose();
    }
  };

  const handleAssignEmail = async (customer, replaceSlot = null) => {
    setAssignmentInProgress(true);
    setAssignmentError('');
    setAssignmentSuccess(false);

    try {
      const payload = {
        customer_id: customer.customer_id,
        email: emailToAssign,
        email_type: 'contact'
      };

      // Add replace_slot if replacing existing email
      if (replaceSlot) {
        payload.replace_slot = replaceSlot;
      }

      const response = await fetch(`${API_URL}/api/customers/assign-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to assign email');
      }

      await response.json();

      setAssignmentSuccess(true);
      setAssignmentError('');

      // Call parent callback if provided
      if (onAssignEmail) {
        onAssignEmail({
          customer_id: customer.customer_id,
          customer_name: customer.customer_name,
          email: emailToAssign,
          success: true
        });
      }

      // Close modal after brief delay
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setAssignmentError(err.message || 'Failed to assign email to customer');
      console.error('Error assigning email:', err);
    } finally {
      setAssignmentInProgress(false);
      setShowReplaceDialog(false);
    }
  };

  const handleRetryAssignment = () => {
    if (selectedCustomerForAssignment) {
      handleAssignEmail(selectedCustomerForAssignment, selectedReplaceSlot);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const formatCooldownTime = (seconds) => {
    const safeSeconds = Math.max(0, Number(seconds) || 0);
    const minutes = Math.floor(safeSeconds / 60);
    const remainingSeconds = safeSeconds % 60;
    if (minutes === 0) {
      return `${remainingSeconds}s`;
    }
    return `${minutes}m ${remainingSeconds}s`;
  };

  const handleSyncCustomers = async () => {
    setSyncInProgress(true);
    setSyncMessage('');
    setSyncError('');

    try {
      const response = await fetch(`${API_URL}/api/printlogic-customers/sync`, {
        method: 'POST',
        credentials: 'include'
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (response.status === 429) {
          const cooldown = formatCooldownTime(payload.cooldown_remaining_seconds);
          throw new Error(`Sync is on cooldown. Try again in ${cooldown}.`);
        }

        throw new Error(payload.error || 'Failed to sync Printlogic customers');
      }

      const summary = `Sync complete: ${payload.inserted || 0} added, ${payload.skipped_existing || 0} already existed, ${payload.failed || 0} failed.`;
      setSyncMessage(summary);
      await loadCustomers(searchQuery);
    } catch (err) {
      setSyncError(err.message || 'Failed to sync customers');
      console.error('Error syncing Printlogic customers:', err);
    } finally {
      setSyncInProgress(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  // Timeout handling for email assignment mode
  useEffect(() => {
    if (isOpen && showEmailAssignment) {
      // 5 minute timeout
      const timeoutDuration = 5 * 60 * 1000; // 5 minutes
      const warningTime = 4.5 * 60 * 1000; // 4.5 minutes

      // Show warning at 4:30
      const warningTimer = setTimeout(() => {
        setTimeoutWarning(true);
      }, warningTime);

      // Auto-close at 5:00
      const timeoutTimer = setTimeout(() => {
        console.log('Dialog timed out after 5 minutes');
        onClose();
      }, timeoutDuration);

      return () => {
        clearTimeout(warningTimer);
        clearTimeout(timeoutTimer);
      };
    }
  }, [isOpen, showEmailAssignment, onClose]);

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog 
        as="div" 
        className="relative z-[60]" 
        onClose={onClose}
      >
        {/* Background overlay */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 pointer-events-none" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 mb-4">
                  {showEmailAssignment ? 'Assign Email to Customer' : 'Select Printlogic Customer'}
                </Dialog.Title>

                <div className="mb-4 text-sm text-gray-600">
                  {showEmailAssignment
                    ? 'Search for a customer and select one to assign this email address to their account.'
                    : 'Select a customer to assign this quote for Printlogic submission.'}
                </div>

                {/* Email Assignment Header */}
                {showEmailAssignment && emailToAssign && (
                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded">
                    <div className="flex items-start">
                      <svg className="w-5 h-5 text-blue-500 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-blue-900">
                          Assigning Email Address
                        </div>
                        <div className="text-sm text-blue-700 mt-1 font-mono break-all">
                          {emailToAssign}
                        </div>
                        <div className="text-xs text-blue-600 mt-2">
                          Select a customer to assign this email address to their account.
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Timeout Warning */}
                {timeoutWarning && (
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded">
                    <div className="flex items-center text-amber-800 text-sm">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                      </svg>
                      This dialog will automatically close in 30 seconds due to inactivity.
                    </div>
                  </div>
                )}

                {/* Assignment Success */}
                {assignmentSuccess && (
                  <div className="mb-4 p-3 bg-green-100 text-green-700 rounded text-sm">
                    <div className="flex items-start">
                      <svg className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Email successfully assigned to customer!
                    </div>
                  </div>
                )}

                {/* Assignment Error */}
                {assignmentError && (
                  <div className="mb-4 p-3 bg-red-100 text-red-700 rounded text-sm">
                    <div className="flex items-start">
                      <svg className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <div className="flex-1">
                        <div>{assignmentError}</div>
                        <button
                          onClick={handleRetryAssignment}
                          className="mt-2 text-sm underline hover:no-underline"
                        >
                          Retry
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Search controls */}
                <div className="mb-4 space-y-2">
                  <div className="relative">
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Search customers by name..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                      className="w-full p-2 pl-8 pr-8 border rounded focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <svg className="w-4 h-4 absolute left-2 top-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={clearSearch}
                        className="absolute right-2 top-2 p-1 text-gray-400 hover:text-gray-600"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                  
                  <div className="flex justify-between items-center gap-2">
                    <span className="text-sm text-gray-600">
                      {loading ? 'Searching...' : searchQuery ? `${customers.length} customer${customers.length !== 1 ? 's' : ''} found` : 'Start typing to search for customers'}
                    </span>
                    <div className="flex items-center gap-2">
                      {searchQuery && customers.length >= 50 && (
                        <span className="text-xs text-amber-600">
                          Showing first 50 results. Refine search for more.
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={handleSyncCustomers}
                        disabled={syncInProgress || assignmentInProgress}
                        className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        {syncInProgress ? 'Syncing...' : 'Sync from Printlogic'}
                      </button>
                    </div>
                  </div>
                </div>

                {syncMessage && (
                  <div className="mb-4 p-3 bg-green-100 text-green-700 rounded text-sm">
                    {syncMessage}
                  </div>
                )}

                {syncError && (
                  <div className="mb-4 p-3 bg-amber-100 text-amber-800 rounded text-sm">
                    {syncError}
                  </div>
                )}
                
                {/* Error message */}
                {error && (
                  <div className="mb-4 p-3 bg-red-100 text-red-700 rounded text-sm">
                    <div className="flex items-start">
                      <svg className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      {error}
                    </div>
                  </div>
                )}
                
                {/* Customer list */}
                <div className="max-h-80 overflow-y-auto">
                  {loading ? (
                    <div className="text-center py-8 text-gray-500">
                      <div className="inline-flex items-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Searching customers...
                      </div>
                    </div>
                  ) : customers.length > 0 ? (
                    <div className="space-y-1">
                      {customers.map(customer => (
                        <button
                          key={customer.customer_id}
                          type="button"
                          className="w-full text-left p-3 border rounded hover:bg-gray-50 focus:bg-blue-50 focus:border-blue-300 focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={() => handleSelectCustomer(customer)}
                          disabled={assignmentInProgress}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-900 truncate">
                                {customer.customer_name}
                              </div>
                              <div className="text-sm text-gray-500 mt-0.5">
                                ID: {customer.customer_id}
                              </div>

                              {/* Pricing tier badge - only show when NOT in email assignment mode */}
                              {!showEmailAssignment && customer.pricing_tier && (
                                <div className="mt-2">
                                  <span className={`inline-block text-xs px-2 py-1 rounded font-medium ${
                                    customer.pricing_tier === 'partner'
                                      ? 'bg-blue-100 text-blue-800'
                                      : customer.pricing_tier === 'trade'
                                      ? 'bg-purple-100 text-purple-800'
                                      : customer.pricing_tier === 'choosyg'
                                      ? 'bg-teal-100 text-teal-800'
                                      : customer.pricing_tier === 'retail'
                                      ? 'text-gray-800'
                                      : 'bg-gray-100 text-gray-800'
                                  }`} style={customer.pricing_tier === 'retail'
                                    ? { backgroundColor: 'rgba(250, 250, 0, 0.25)', color: '#6b6b00' }
                                    : undefined}>
                                    {customer.pricing_tier === 'partner' ? 'Partner' :
                                     customer.pricing_tier === 'trade' ? 'Trade' :
                                     customer.pricing_tier === 'choosyg' ? 'Choosy Giant' : 'Retail'}
                                  </span>
                                </div>
                              )}

                              {/* Email count display in assignment mode */}
                              {showEmailAssignment && customer.emails && (
                                <div className="text-xs text-gray-600 mt-1">
                                  {customer.emails.length} email{customer.emails.length !== 1 ? 's' : ''} assigned
                                  {customer.emails.length >= 10 && (
                                    <span className="ml-2 text-amber-600 font-medium">
                                      (at limit - will need to replace)
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="ml-4 flex-shrink-0">
                              {assignmentInProgress ? (
                                <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              ) : (
                                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : !loading && searchQuery ? (
                    <div className="text-center py-8 text-gray-500">
                      <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <p className="mb-2">No customers found matching "{searchQuery}"</p>
                      <p className="text-sm">Try a different search term or check spelling.</p>
                    </div>
                  ) : !loading ? (
                    <div className="text-center py-8 text-gray-500">
                      <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <p className="mb-2">Start typing to search for customers</p>
                      <p className="text-sm">Search by customer name to find and select a customer.</p>
                    </div>
                  ) : null}
                </div>
                
                {/* Modal actions */}
                <div className="mt-6 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>

      {/* Email Replacement Dialog */}
      {showReplaceDialog && selectedCustomerForAssignment && (
        <Transition show={showReplaceDialog} as={Fragment}>
          <Dialog as="div" className="relative z-[70]" onClose={() => setShowReplaceDialog(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/30 pointer-events-none" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto pointer-events-none">
              <div className="flex min-h-full items-center justify-center p-4 pointer-events-auto">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 mb-4">
                      Replace Existing Email
                    </Dialog.Title>

                    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
                      <div className="flex items-start">
                        <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <strong>{selectedCustomerForAssignment.customer_name}</strong> has reached the maximum of 10 email addresses. Select an existing email to replace.
                        </div>
                      </div>
                    </div>

                    <div className="mb-4 text-sm text-gray-700">
                      <strong>New email to assign:</strong>
                      <div className="mt-1 p-2 bg-green-50 border border-green-200 rounded font-mono text-green-800 break-all">
                        {emailToAssign}
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select email to replace:
                      </label>
                      <div className="max-h-60 overflow-y-auto space-y-1 border rounded p-2">
                        {selectedCustomerForAssignment.emails && selectedCustomerForAssignment.emails.map((emailObj) => (
                          <label
                            key={emailObj.id}
                            className={`flex items-start p-2 rounded cursor-pointer hover:bg-gray-50 ${
                              selectedReplaceSlot === emailObj.id ? 'bg-blue-50 border border-blue-300' : 'border border-transparent'
                            }`}
                          >
                            <input
                              type="radio"
                              name="replace-email"
                              value={emailObj.id}
                              checked={selectedReplaceSlot === emailObj.id}
                              onChange={() => setSelectedReplaceSlot(emailObj.id)}
                              className="mt-1 mr-2"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-mono text-gray-900 break-all">
                                {emailObj.email}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                Type: {emailObj.email_type}
                                {emailObj.is_primary && (
                                  <span className="ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">Primary</span>
                                )}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setShowReplaceDialog(false);
                          setSelectedReplaceSlot(null);
                        }}
                        className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
                        disabled={assignmentInProgress}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAssignEmail(selectedCustomerForAssignment, selectedReplaceSlot)}
                        disabled={!selectedReplaceSlot || assignmentInProgress}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                      >
                        {assignmentInProgress ? 'Replacing...' : 'Replace Email'}
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      )}
    </Transition>
  );
};

export default PrintlogicCustomerModal;
